import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { reduxForm, Field, change } from 'redux-form';
import { connect } from 'react-redux';
import { SelectInput } from 'components/Common/Inputs';
import FileUpload from 'components/Common/FileUpload';
import { ApiService } from 'services';
import { toastr } from 'react-redux-toastr';

const hasWhdStatement = (value, values) => (values.witholdingStmnt && (value === 'undefined' || value === null));

class WithholdingStatementUploadForm extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    statementType: PropTypes.string,
    onFileUploadCancel: PropTypes.func.isRequired,
    onFileUploadError: PropTypes.func.isRequired,
    onFileUploadSuccess: PropTypes.func.isRequired,
    onStatementTypeChange: PropTypes.func.isRequired,
    onFileDeleteSuccess: PropTypes.func,
    onFileDeleteError: PropTypes.func,
    formErrors: PropTypes.oneOfType([PropTypes.bool, PropTypes.arrayOf(PropTypes.any)]),
    disableToastr: PropTypes.bool,
    ddLabelText: PropTypes.string,
    existingStatements: PropTypes.any,
    disableConfirm: PropTypes.func.isRequired,
    singleColumnLayout: PropTypes.bool
  };

  static defaultProps = {
    statementType: null,
    formErrors: [],
    disableToastr: false,
    onFileDeleteError: null,
    onFileDeleteSuccess: null,
    ddLabelText: 'WHD Stmt Type',
    existingStatements: null,
    singleColumnLayout: true
  };

  constructor(props) {
    super(props);

    this.state = {
      whdStatementType: props.statementType,
      whdStatement: null,
      whdStatementError: null,
      whdStatementUploading: false
    };
  }

  componentDidUpdate(prevProps) {
    const { statementType } = this.props;
    const { whdStatementType } = this.state;

    if (prevProps.statementType !== statementType && statementType !== whdStatementType) {
      this.resetStatement();
    }
  }

  onWhdStatementTypeChange(e, newValue) {
    const { onStatementTypeChange } = this.props;
    this.setState({ whdStatementType: newValue });
    if (onStatementTypeChange) {
      onStatementTypeChange(e, newValue);
    }
  }

  onWhdStatementFileUploadCancel(file) {
    const { onFileUploadCancel, disableConfirm } = this.props;

    if (onFileUploadCancel) {
      disableConfirm(false);
      onFileUploadCancel(file);
    }
  }

  onWhdStatementFileUploadError(file, response) {
    const { onFileUploadError, disableConfirm } = this.props;

    const fileDetails = {
      name: file.name,
      size: file.size,
      lastModified: file.lastModified
    };

    let whdError = {
      ...response.result,
      file: fileDetails
    };

    if (!response.result || (response.result && response.result.resultCode === 10)) { // Unlicensed
      whdError = null;
      toastr.error('', response.message);
    }

    this.setState({ whdStatement: null });
    this.setState({ whdStatementError: whdError });
    this.setState({ whdStatementUploading: false });

    if (onFileUploadError) {
      disableConfirm(false);
      onFileUploadError(file, response);
    }
  }

  onWhdStatementFileSending(file) {
    const { disableConfirm } = this.props;
    if (file) {
      disableConfirm(true);
      this.setState({ whdStatementError: null });
      this.setState({ whdStatementUploading: true });
    }
  }

  onWhdStatementFileUploadSuccess(file, response) {
    const { disableToastr, onFileUploadSuccess, disableConfirm } = this.props;
    this.setState({ whdStatement: response });
    this.setState({ whdStatementError: null });
    this.setState({ whdStatementUploading: false });

    if (!disableToastr) {
      if (response && response.file && response.file.fileName) {
        disableConfirm(false);
        toastr.success('', `${ response.file.fileName } has been uploaded`);
      } else {
        toastr.error('', `${ (file && file.name) || 'WHD Statement' } failed to upload`);
      }
    }

    if (onFileUploadSuccess) {
      onFileUploadSuccess(file, response);
    }
  }

  onDeleteWhdStatement(file) {
    const { disableToastr, onFileDeleteError, onFileDeleteSuccess } = this.props;
    ApiService
      .deleteTempUpload(file.id)
      .then((result) => {
        if (result != null) {
          this.setState({ whdStatement: null });
        } else if (!disableToastr) {
          toastr.error('', `Failed to remove ${ file.name }.  Please try again`);
        }
        if (onFileDeleteSuccess) {
          onFileDeleteSuccess(file);
        }
      })
      .catch((err) => {
        if (!disableToastr) {
          toastr.error('', `Failed to remove ${ file.name }.  Please try again`);
        }
        if (onFileDeleteError) {
          onFileDeleteError(file, err);
        }
      });
  }

  resetStatement() {
    const { dispatch, statementType } = this.props;

    this.setState({
      whdStatementType: statementType,
      whdStatement: null
    }, () => {
      dispatch(change('whdStmtUploadForm', 'statementType', statementType));
    });
  }

  isPooledStatement() {
    const { whdStatement } = this.state;
    return whdStatement && whdStatement.isValid && whdStatement.file && (whdStatement.file.type === 'QiPooled' || whdStatement.file.type === 'NqiPooled' || whdStatement.file.type === 'PooledWhdStatement');
  }

  fieldHasErrors(field) {
    const { formErrors } = this.props;
    return formErrors != null && formErrors.filter(error => error.field === field).length > 0;
  }

  render() {
    const {
      ddLabelText,
      existingStatements,
      singleColumnLayout
    } = this.props;
    const {
      whdStatement,
      whdStatementError,
      whdStatementType,
      whdStatementUploading
    } = this.state;

    return (<React.Fragment>
      { /* WHD Statement Upload --> */ }
      { existingStatements != null && existingStatements.length > 0 && <div className="form-group">
        <div className="col-12">
          <div className="card card-error">
            <div className="card-header">
              <h2>Existing WHD Statement{ existingStatements.length > 1 && <React.Fragment>s</React.Fragment> } Found</h2>
            </div>
            <div className="card-body">
              <p>Cannot upload new withholding statement as one is already present for the account{ existingStatements.length > 1 && <React.Fragment>s listed</React.Fragment> } below.</p>
              <div className="reasons-for-rejection-list">
                <ul className="mt-0">
                  { existingStatements.map(x => <li>
                    <strong>Account: </strong>{ x.account.accountNumber } - { x.account.accountName }<br />
                    <strong>Intermediary</strong>: { x.withholdingStatement.intermediaryName }, { x.withholdingStatement.intermediaryCountry }
                  </li>) }
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>}
      { (existingStatements == null || existingStatements.length === 0) && <div className="form-group">
        <div className={ ` ${ singleColumnLayout ? 'col-6 col-sm-12 pr-0' : '' } ` }>
          <div className="form-group">
            <div className={ ` ${ singleColumnLayout ? 'col-4 col-sm-12 pr-2' : '' } ` }>
              <label htmlFor="whd-statement-type" className="form-label">{ ddLabelText }</label>
            </div>
            <div className={ ` ${ singleColumnLayout ? 'col-8 col-sm-12 pr-2' : '' } ` }>
              <Field
                id="whd-statement-type"
                name="statementType"
                component={ SelectInput }
                className={ `form-select ${ this.fieldHasErrors('whdStmtType') ? 'is-error' : '' }` }
                validate={ hasWhdStatement }
                value={ whdStatementType ?? '' }
                disabled={ whdStatementUploading || !!whdStatement }
                onChange={ (e, newValue) => this.onWhdStatementTypeChange(e, newValue) }
              >
                <option value="">Choose a statement type</option>
                <option value="altwhdstatement">Alt Statement</option>
                <option value="detailedwhdstatement">Detailed Statement</option>
                <option value="simplewhdstatement">Simple Statement</option>
                <option value="pooledwhdstatement">Pooled Statement</option>
              </Field>
            </div>
          </div>
        </div>
        { whdStatementType !== '' && whdStatementType != null && <div className={ ` ${ singleColumnLayout ? 'col-6 col-sm-12' : '' } ` }>
          <div className="form-group columns">
            { whdStatement == null && <div className="col-12">
              <FileUpload
                defaultMessage="Drop the WHD Statement here to upload in TAINA's standard format"
                postUrl={ ApiService.getFileUploadUrl() }
                accessTokenName={ ApiService.getAccessTokenName() }
                acceptedFiles="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .xlsx"
                maxFilesize={ 5 }
                maxUploadFiles={ 1 }
                timeout={ 60000 }
                docCategory="withholdingstatement"
                docType={ whdStatementType != null ? whdStatementType : '' }
                removeFileAfterUpload
                onFileSending={ file => this.onWhdStatementFileSending(file) }
                onFileUploadCancel={ file => this.onWhdStatementFileUploadCancel(file) }
                onFileUploadSuccess={ (file, response) => this.onWhdStatementFileUploadSuccess(file, response) }
                onFileUploadError={ (file, error) => this.onWhdStatementFileUploadError(file, error) }
              />
            </div> }
            { whdStatement != null && <div className="col-4 mt-2">
              <label htmlFor="pdf-form-file" className="form-label">WHD Stmt</label>
            </div> }
            { whdStatement != null && <div className="col-8 mt-2">
              <span className="whd-file-name ellipsis">{ whdStatement.file.fileName }</span>
              <button
                className="btn btn-sm btn-danger text-right"
                type="button"
                title="Delete WHD statement"
                onClick={ () => this.onDeleteWhdStatement(whdStatement.file) }
              >
                <span className="icon icon-delete" />
              </button>
            </div> }
          </div>
        </div> }
        { this.isPooledStatement(whdStatement) && <div className="col-12 pt-2 whd-statement-validation">
          <div className="card card-success">
            <div className="card-header">
              <h2>Withholding Statement Validation Successful</h2>
            </div>
            <div className="card-body">
              <p>Intermediary: <strong>{ whdStatement.statement.intermediaryName }</strong></p>
              <div>
                <p>Accounts:</p>
                <ul className="mt-0">
                  { whdStatement.statement.listOfAccounts && whdStatement.statement.listOfAccounts.map((a, i) => <li key={ (a && `${ a.accountNumber }-${ a.accountName }`) || i }>
                    { a.accountName }
                  </li>) }
                  { whdStatement.statement.accounts && whdStatement.statement.accounts.map((a, i) => <li key={ (a && `${ a.accountNumber }-${ a.accountName }`) || i }>
                    { a.accountName }
                  </li>) }
                </ul>
              </div>
            </div>
          </div>
        </div> }
        { whdStatementError != null && !whdStatementError.isValid && <div className="col-12 pt-2 whd-statement-validation">
          <div className="card card-error">
            <div className="card-header">
              <h2>WHD Statement Validation Failed</h2>
              { whdStatementError.file && <p><strong>File: </strong>{ whdStatementError.file.name } ({ whdStatementError.file.size / 1024 }kb)</p> }
            </div>
            <div className="card-body">
              <div className="reasons-for-rejection-list"><ul>
                { whdStatementError.validationResults && whdStatementError.validationResults.map(item => <li key={ `${ item.identifier ?? '' }-${ item.code }` }>
                  <em>{ item.codeDescription.replace('The withholding statement has failed validation. ', '') }</em>&nbsp;(<strong>{ item.code }</strong>)
                </li>) }
                { (!whdStatementError.validationResults || whdStatementError.validationResults.length === 0) && <li>
                  <em>The withholding statement failed to upload, but no error details were returned</em>
                </li> }
              </ul></div>
            </div>
          </div>
        </div> }
      </div> }
    </React.Fragment>);
  }
}

const WhdStmtUploadReduxForm = reduxForm({
  form: 'whdStmtUploadForm',
})(WithholdingStatementUploadForm);

const mapDispatchToProps = dispatch => ({ dispatch });

export const WithholdingStatementUpload = connect(
  null,
  mapDispatchToProps
)(WhdStmtUploadReduxForm);

export default WithholdingStatementUpload;
